import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

const languages = [
  { code: 'pt', label: 'Português', flag: 'BR' },
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'de', label: 'Deutsch', flag: 'DE' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    const close = () => {
      setCatOpen(false);
      setLangOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const navBg = scrolled
    ? 'bg-white/95 backdrop-blur-md border-b border-background-200/70'
    : 'bg-white border-b border-background-200/70';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="container-main flex items-center justify-between h-16 md:h-18">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 shrink-0">
          <span className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 tracking-tight">
            CSSBuy<span className="text-primary-500">BR</span>
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-5">
          <a href="/" className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap">
            {t('nav.home')}
          </a>
          <a href="/cssbuy-spreadsheet/" className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap">
            {t('nav.guides')}
          </a>
          <a href="/products/" className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap">
            {t('nav.products')}
          </a>

          {/* Categories Dropdown */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setCatOpen(!catOpen); setLangOpen(false); }}
              className="flex items-center gap-1 text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {t('nav.categories')}
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {catOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-background-200/70 shadow-lg overflow-hidden">
                <div className="max-h-80 overflow-y-auto py-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors text-sm text-foreground-800"
                    >
                      <img src={cat.image} alt={cat.name} className="w-8 h-8 rounded-md object-cover object-top shrink-0" loading="lazy" />
                      <span className="truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a href="/cssbuy-review/" className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap">
            {t('nav.review')}
          </a>
          <a href="/cssbuy-shipping-cost/" className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap">
            {t('nav.shipping')}
          </a>
          <a href="/safety/" className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap">
            {t('nav.safety')}
          </a>
        </div>

        {/* Right Side */}
        <div className="hidden lg:flex items-center gap-3">
          {/* Language Switcher */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setLangOpen(!langOpen); setCatOpen(false); }}
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors px-3 py-1.5 rounded-full border border-background-200/70"
            >
              <i className="ri-global-line"></i>
              {languages.find(l => l.code === i18n.language)?.label || 'Português'}
              <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 mt-2 w-40 bg-white rounded-xl border border-background-200/70 shadow-lg overflow-hidden py-1">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLang(lang.code)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors ${i18n.language === lang.code ? 'text-primary-500 font-medium bg-primary-50/50' : 'text-foreground-700'}`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            {t('nav.visitMain')}
            <i className="ri-external-link-line text-xs"></i>
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden p-2 text-foreground-900"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-background-200/70 px-4 pb-6 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="flex flex-col gap-1 pt-4">
            <a href="/" className="px-3 py-2.5 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100">
              {t('nav.home')}
            </a>
            <a href="/cssbuy-spreadsheet/" className="px-3 py-2.5 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100">
              {t('nav.guides')}
            </a>
            <a href="/products/" className="px-3 py-2.5 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100">
              {t('nav.products')}
            </a>
            <a href="/cssbuy-review/" className="px-3 py-2.5 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100">
              {t('nav.review')}
            </a>
            <a href="/cssbuy-shipping-cost/" className="px-3 py-2.5 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100">
              {t('nav.shipping')}
            </a>
            <a href="/safety/" className="px-3 py-2.5 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100">
              {t('nav.safety')}
            </a>

            {/* Mobile Categories Accordion */}
            <div className="border-t border-background-200/70 my-2 pt-2">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100"
              >
                {t('nav.categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="grid grid-cols-2 gap-2 px-3 pt-2 pb-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 p-2 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors"
                    >
                      <img src={cat.image} alt={cat.name} className="w-9 h-9 rounded-md object-cover object-top shrink-0" loading="lazy" />
                      <span className="text-xs font-medium text-foreground-800 leading-tight">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200/70 my-2 pt-2 flex items-center gap-2 px-3">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLang(lang.code)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${i18n.language === lang.code ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-700'}`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 mx-3 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full text-center hover:bg-primary-600 transition-colors"
            >
              {t('nav.visitMain')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}